import { useCallback, useEffect, useRef, useState } from "react";
import { escapeAttrForSelector } from "../utils/domSelectorEscape";

function buildAttrSelector(attr, value) {
  if (value == null || attr == null) return null;
  const raw = String(value);
  const safe =
    typeof CSS !== "undefined" && CSS.escape ? CSS.escape(raw) : escapeAttrForSelector(raw);
  return `[${attr}="${safe}"]`;
}

/**
 * Horizontal scroll strip: measure overflow, optional scroll-into-view for active item, scrollBy helper.
 *
 * @param {Object} options
 * @param {'always'|'when-overflow'} [options.arrowsVisibility] Show chevrons always vs only when content overflows.
 * @param {unknown} [options.layoutDependency] When this identity changes, scroll metrics are recalculated.
 * @param {unknown} [options.activeItemId] Active tab key / step id for scrollIntoView.
 * @param {string | null} [options.scrollIntoViewAttr] e.g. 'data-tab-key' — omit to disable scroll-into-view.
 * @param {boolean} [options.scrollIntoViewEnabled=true] Gate scroll-into-view (e.g. empty list).
 * @param {number} [options.minScrollChunk=180]
 * @param {number} [options.scrollViewportRatio=0.65] Fraction of clientWidth per arrow click (when-overflow uses ratio from options).
 * @param {number} [options.scrollIntoViewDelayMs=50]
 * @param {number} [options.scrollStateRefreshMs=320] After programmatic scrollBy.
 */
export function useHorizontalScrollNav({
  arrowsVisibility = "always",
  layoutDependency,
  activeItemId,
  scrollIntoViewAttr = null,
  scrollIntoViewEnabled = true,
  minScrollChunk = 180,
  scrollViewportRatio = 0.65,
  scrollIntoViewDelayMs = 50,
  scrollStateRefreshMs = 320
}) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [showOverflowArrows, setShowOverflowArrows] = useState(arrowsVisibility === "always");

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      if (arrowsVisibility === "when-overflow") setShowOverflowArrows(false);
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    const epsilon = 2;
    const overflowing = maxScroll > epsilon;

    if (arrowsVisibility === "when-overflow") {
      setShowOverflowArrows(overflowing);
      setCanScrollLeft(overflowing && scrollLeft > epsilon);
      setCanScrollRight(overflowing && scrollLeft < maxScroll - epsilon);
    } else {
      setShowOverflowArrows(true);
      setCanScrollLeft(scrollLeft > epsilon);
      setCanScrollRight(overflowing && scrollLeft < maxScroll - epsilon);
    }
  }, [arrowsVisibility]);

  useEffect(() => {
    updateScrollState();
  }, [layoutDependency, updateScrollState]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const onScroll = () => updateScrollState();
    el.addEventListener("scroll", onScroll, { passive: true });

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => updateScrollState());
      ro.observe(el);
    } else {
      window.addEventListener("resize", updateScrollState);
    }

    return () => {
      el.removeEventListener("scroll", onScroll);
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  useEffect(() => {
    if (!scrollIntoViewAttr || !scrollIntoViewEnabled) return undefined;
    if (activeItemId == null) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;

    const selector = buildAttrSelector(scrollIntoViewAttr, activeItemId);
    if (!selector) return undefined;

    const t = window.setTimeout(() => {
      const node = el.querySelector(selector);
      if (node && typeof node.scrollIntoView === "function") {
        node.scrollIntoView({
          inline: "nearest",
          block: "nearest",
          behavior: "smooth"
        });
      }
      updateScrollState();
    }, scrollIntoViewDelayMs);

    return () => window.clearTimeout(t);
  }, [
    activeItemId,
    layoutDependency,
    scrollIntoViewAttr,
    scrollIntoViewEnabled,
    scrollIntoViewDelayMs,
    updateScrollState
  ]);

  const scrollByDir = useCallback(
    dir => {
      const el = scrollRef.current;
      if (!el) return;
      const delta = Math.max(minScrollChunk, Math.floor(el.clientWidth * scrollViewportRatio)) * dir;
      el.scrollBy({ left: delta, behavior: "smooth" });
      window.setTimeout(updateScrollState, scrollStateRefreshMs);
    },
    [minScrollChunk, scrollViewportRatio, scrollStateRefreshMs, updateScrollState]
  );

  const showArrows =
    arrowsVisibility === "always" ? true : showOverflowArrows;

  return {
    scrollRef,
    canScrollLeft,
    canScrollRight,
    showArrows,
    scrollByDir,
    updateScrollState
  };
}
